import React from 'react'
import { HomePullReveal } from '../../components/home/HomePullReveal'
import { usePullToRefresh } from '../../hooks/usePullToRefresh'
import {
  View,
  Text,
  FlatList,
  RefreshControl,
  TouchableOpacity,
  useWindowDimensions,
} from 'react-native'
import { useRouter, useFocusEffect, useLocalSearchParams } from 'expo-router'
import { SafeAreaView } from 'react-native-safe-area-context'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { BrandMark } from '@warcarp/ui'
import { useOrientation } from '../../hooks/useOrientation'
import { useAuth } from '../../hooks/useAuth'
import { useGames, useLobbyGames } from '../../hooks/useGames'
import { useTheme } from '../../hooks/useTheme'
import { Button } from '../../components/ui/Button'
import { AppModal } from '../../components/ui/AppModal'
import { apiPost } from '../../lib/api'
import { removeItem, setItem } from '../../lib/storage'
import { TUTORIAL_WELCOME_KEY } from '../../lib/tutorial'
import { reportError } from '../../lib/reporting'
import { GameRow, GameSkeletonRow } from '../../components/GameRow'
import { isCompleteGame, byCompletedAtDesc, compareCurrentTab } from '../../lib/lobbyTabs'
import { useSupportUnread } from '../../hooks/useSupportInbox'
import { getPublicOpenGames, publicLobbySummary } from '../../lib/publicLobbySummary'
import { isLocalQaEnvironment } from '../../lib/devTunnel'
import { HomeHeader } from '../../components/home/HomeHeader'
import { LogoEggProvider } from '../../components/home/logoEgg/LogoEgg'
import { SAFE_AREA_EDGES_BELOW_FILL, TopInsetFill } from '../../components/ui/TopInsetFill'
import { DesktopCommandColumn, HOME_DESKTOP_MAX_WIDTH, HOME_SIDE_PADDING, HOME_SINGLE_COLUMN_MAX_WIDTH } from '../../components/home/DesktopCommandColumn'

export default function HomeScreen() {
  const supportUnread = useSupportUnread().data?.count ?? 0
  const { user, refreshUser } = useAuth()
  const { theme } = useTheme()
  // Theme ink for the header vector icons (matches the in-game top-bar icons).
  const inkColor = theme === 'dark' ? '#e6e1c4' : '#18170f'
  const bloodColor = theme === 'dark' ? '#e0463f' : '#a01818'
  const { data: games, isLoading, refetch } = useGames()
  const publicLobby = useLobbyGames({ limit: 50, refetchInterval: 60_000 })
  const publicOpenGames = React.useMemo(
    () => getPublicOpenGames(publicLobby.data?.pages.flatMap((page) => page.games) ?? [], user?.id),
    [publicLobby.data, user?.id],
  )
  const totalOpenGameCount = publicLobby.data?.pages[0]?.publicOpenGameCount
  const openGameCount = totalOpenGameCount ?? publicOpenGames.length
  const lobbyHasMore = !!publicLobby.data?.pages.at(-1)?.nextCursor
  const lobbySummary = publicLobbySummary(openGameCount, totalOpenGameCount === undefined && lobbyHasMore)
  // Lobby tabs: "Current" (default) holds everything still in play (lobby,
  // active, pending-invite); "Complete" holds EVERY finished game, sorted
  // most-recently-completed first.
  const [tab, setTab] = React.useState<'current' | 'complete'>('current')
  const router = useRouter()
  const { width: winWidth } = useWindowDimensions()
  const { isLandscape } = useOrientation()
  const scrollTop = React.useRef(0)
  const onScrollOffsetChange = React.useCallback((offset: number) => { scrollTop.current = offset }, [])
  const refreshHome = React.useCallback(async () => { await Promise.allSettled([refetch(), publicLobby.refetch()]) }, [refetch, publicLobby.refetch])
  const { refreshing, onRefresh } = usePullToRefresh(refreshHome)
  const isWideHome = winWidth >= 720
  const [tutorialWelcomeVisible, setTutorialWelcomeVisible] = React.useState(false)
  const { resetTutorial: resetTutorialParam, resetNonce: resetNonceParam } =
    useLocalSearchParams<{ resetTutorial?: string | string[]; resetNonce?: string | string[] }>()
  const resetTutorial = Array.isArray(resetTutorialParam) ? resetTutorialParam[0] : resetTutorialParam
  const resetNonce = Array.isArray(resetNonceParam) ? resetNonceParam[0] : resetNonceParam
  const lastTutorialResetNonce = React.useRef<string | null>(null)
  const localResetRoute = isLocalQaEnvironment()
  const isTutorialResetRoute = localResetRoute && resetTutorial === '1'

  // Local QA escape hatch. The reset script opens this route with a unique
  // nonce so the native welcome marker can be cleared without touching the
  // user's login/session keys. It is available in dev and localhost QA builds.
  React.useEffect(() => {
    if (
      !localResetRoute ||
      resetTutorial !== '1' ||
      !user?.id ||
      lastTutorialResetNonce.current === resetNonce
    ) {
      return
    }
    lastTutorialResetNonce.current = resetNonce ?? 'manual'
    void removeItem(TUTORIAL_WELCOME_KEY(user.id))
      .then(() => setTutorialWelcomeVisible(true))
      .catch((err: unknown) => {
        reportError(err, { tags: { surface: 'home', action: 'reset-tutorial' } })
      })
  }, [localResetRoute, resetNonce, resetTutorial, user?.id])

  React.useEffect(() => {
    if (!user?.id) {
      setTutorialWelcomeVisible(false)
      return
    }

    // The server flag is authoritative here. In particular, a local marker
    // can survive a demo reset (or a reinstall/session handoff) and must not
    // hide the first-login welcome when the account is explicitly unseen.
    if (user.tutorialWelcomeSeen !== false) return

    setTutorialWelcomeVisible(true)
    // Record the first display immediately so the welcome modal is shown once
    // per account. The server flag keeps existing users suppressed across
    // devices and makes a reset deterministic even if local storage is stale.
    void setItem(TUTORIAL_WELCOME_KEY(user.id), '1').catch((err: unknown) => {
      reportError(err, { tags: { surface: 'home', action: 'mark-tutorial-welcome-seen' } })
    })
    // The local reset route is a QA escape hatch: it must leave the server
    // state unseen so the next real sign-in still exercises first-login.
    if (isTutorialResetRoute) return
    void apiPost('/api/v1/auth/me/tutorial-welcome/seen')
      .then(() => refreshUser())
      .catch((err: unknown) => {
        reportError(err, { tags: { surface: 'home', action: 'mark-tutorial-welcome-seen-server' } })
      })
  }, [isTutorialResetRoute, refreshUser, user?.id, user?.tutorialWelcomeSeen])

  const dismissTutorialWelcome = React.useCallback(async () => {
    setTutorialWelcomeVisible(false)
    if (user?.id) await setItem(TUTORIAL_WELCOME_KEY(user.id), '1')
  }, [user?.id])

  const startTutorialFromWelcome = React.useCallback(async () => {
    await dismissTutorialWelcome()
    router.push('/(app)/tutorial' as never)
  }, [dismissTutorialWelcome, router])

  // Stable renderItem so FlatList doesn't unmount/remount rows on parent
  // re-renders (e.g. the focus-triggered refetch). Combined with React.memo
  // on GameRow this limits reconciliation to rows whose data actually changed.
  const renderGameRow = React.useCallback(
    ({ item }: { item: import('@warcarp/api-types').GameResponse }) => (
      <GameRow
        game={item}
        showMapPreview
        currentUserId={user?.id}
        onPress={() => {
          // All tiles — including pending-invite ones — route to the
          // unified LOBBY screen. State B (slot picker + Join button)
          // renders when the viewer is not yet a player.
          router.push(`/(app)/games/${item.id}`)
        }}
      />
    ),
    [user?.id, router],
  )

  // Stale-while-revalidate on focus.
  //
  // We DO kick off a refetch the moment the screen regains focus —
  // the user just came back from a game they may have resigned /
  // started / finished, and stale-data-by-staleTime alone could
  // leave that change invisible for up to 30 s. The refetch fires
  // immediately at the start of the navigation.
  //
  // What we DON'T do is show a loading state. React Query keeps the
  // previously-cached list in `data` while the network call is in
  // flight; the RefreshControl below is bound to a spinner condition
  // that's only true on the very first load (no cached games yet) OR
  // on explicit pull-to-refresh — never on a focus-driven background
  // refetch. So the user sees the old list instantly, then the list
  // updates atomically when the request returns. The previous
  // implementation tied the spinner to isLoading on every focus,
  // which is what the user called out as the "wait to load" UX.
  useFocusEffect(
    React.useCallback(() => {
      void refetch()
      void publicLobby.refetch()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []),
  )

  const visibleGames = React.useMemo(() => (games ?? [])
    .filter(game => tab === 'complete' ? isCompleteGame(game) : !isCompleteGame(game))
    .sort((a, b) => tab === 'complete' ? byCompletedAtDesc(a, b) : compareCurrentTab(a, b, user?.id)), [games, tab, user?.id])
  const gameTabs = (<>
      {/* Session list tabs — Current (default) vs all completed games. */}
      <View className={isWideHome ? 'flex-row items-center justify-between gap-2' : 'px-4 pt-3 pb-2 flex-row items-center justify-between gap-2'}>
        <View className="flex-row items-center rounded-full border border-olive bg-paper p-1">
        {(['current', 'complete'] as const).map((t) => {
          const selected = tab === t
          return (
            <TouchableOpacity
              key={t}
              onPress={() => setTab(t)}
              accessibilityLabel={t === 'current' ? 'Current games tab' : 'Completed games tab'}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              className={`px-3 py-1 rounded-full ${
                selected ? 'bg-blood' : ''
              }`}
            >
              <Text
                className={`font-stencil text-[10px] uppercase tracking-wider ${
                  selected ? 'text-white' : 'text-ink-soft'
                }`}
              >
                {t === 'current' ? 'Current' : 'Completed'}
              </Text>
            </TouchableOpacity>
          )
        })}
        </View>
        {!isWideHome && openGameCount > 0 && <TouchableOpacity
          accessibilityRole="button"
          accessibilityLabel={lobbySummary.accessibilityLabel}
          onPress={() => router.push('/(app)/games')}
          className="shrink min-w-0 flex-row items-center gap-1.5 rounded-full border border-olive bg-paper px-2.5 py-1.5"
        >
          <View className="shrink min-w-0 flex-row items-center gap-1">
            <MaterialCommunityIcons name="account-multiple-plus" size={16} color={inkColor} />
            <Text numberOfLines={1} className="font-stencil text-blood text-[10px] uppercase tracking-wide">{lobbySummary.label}</Text>
          </View>
          <MaterialCommunityIcons name="chevron-double-right" size={16} color={bloodColor} />
        </TouchableOpacity>}
      </View>

  </>)

  return (
    <SafeAreaView className="flex-1 bg-canvas" edges={SAFE_AREA_EDGES_BELOW_FILL}>
      {/* The logo easter egg's overlay spans the whole screen (it swims over the lobby). */}
      <LogoEggProvider userId={user?.id}>
      <TopInsetFill />
      {/* The pull-to-refresh gesture at the top of the page (header included) uncovers the pull secret. */}
      <HomePullReveal scrollTop={scrollTop} onRefresh={onRefresh}>
      <HomeHeader displayName={user?.displayName} supportUnread={supportUnread} desktop={isWideHome}
        // Same cap as the content below: two columns when open games exist, else the single centred list.
        maxWidth={isWideHome ? (openGameCount > 0 ? HOME_DESKTOP_MAX_WIDTH : HOME_SINGLE_COLUMN_MAX_WIDTH) : undefined}
        sidePadding={isWideHome ? (winWidth >= 1024 ? HOME_SIDE_PADDING.desktop : HOME_SIDE_PADDING.tablet) : undefined}
        onMaps={() => router.push('/(app)/maps')} onBots={() => router.push('/(app)/bots')}
        onHallOfFame={() => router.push('/(app)/hof')} onSettings={user ? () => router.push('/(app)/settings') : undefined} />
      {isWideHome ? <DesktopCommandColumn games={visibleGames} completed={tab === 'complete'}
        currentUserId={user?.id} openGames={publicOpenGames} openGameCount={totalOpenGameCount} hasMoreOpenGames={lobbyHasMore}
        tabs={gameTabs} loading={isLoading} refreshing={refreshing}
        onRefresh={onRefresh} onScrollOffsetChange={onScrollOffsetChange}
        onGamePress={game => router.push(`/(app)/games/${game.id}`)}
        onNewGame={() => router.push('/(app)/games/new')} onJoinGame={() => router.push('/(app)/games')} /> : <>
      {gameTabs}

      {/* Game list — flexes to fill the space between the header
          above and the pinned-at-bottom action buttons below. The
          FlatList itself scrolls; the buttons stay put. */}
      <FlatList
        className="flex-1"
        onScroll={event => onScrollOffsetChange(event.nativeEvent.contentOffset.y)} scrollEventThrottle={16}
        data={visibleGames}
        keyExtractor={(g) => g.id}
        renderItem={renderGameRow}
        contentContainerClassName="px-4 gap-3 pt-2 pb-8"
        refreshControl={
          // Spin while the user explicitly pulls to refresh OR on the
          // very first load (cached data unavailable). On focus
          // revisits with cached data, isLoading is false and
          // isFetching may briefly flip to true — we want the existing
          // list visible without a spinner ribbon during that window.
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor="#ef4444"
          />
        }
        ListEmptyComponent={
          isLoading ? (
            // First load with no cached games — show skeleton placeholders
            // so the screen reads as "loading" rather than "empty".
            <View className="gap-3" accessibilityLabel="Loading games">
              {[0, 1, 2, 3].map((i) => (
                <GameSkeletonRow key={i} />
              ))}
            </View>
          ) : (
            <View className="items-center py-16">
              <Text className="text-ink-soft text-base font-typed">No games yet.</Text>
              <Text className="text-ink-soft text-sm font-typed">Create or join one below.</Text>
            </View>
          )
        }
      />

      {/* Pinned action footer — sits below the scrolling games list
          so the New Game / Join Game CTAs are always reachable even
          on long lists. In landscape the two buttons sit side-by-side
          and are width-capped so they don't stretch to 900+ px. */}
      <View className="border-t border-olive bg-canvas">
        <View
          className={isLandscape ? 'flex-row gap-3' : 'gap-3'}
          style={
            isLandscape
              ? {
                  alignSelf: 'center',
                  width: Math.min(560, winWidth * 0.6),
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                }
              : { paddingHorizontal: 16, paddingVertical: 12 }
          }
        >
          <View className={isLandscape ? 'flex-1' : undefined}>
          <Button title="New Game" textColor="#fff" onPress={() => router.push('/(app)/games/new')} />
          </View>
          <View className={isLandscape ? 'flex-1' : undefined}>
            <Button
              title="Join Game"
              variant="secondary"
              textClassName="font-stencil tracking-wider"
              onPress={() => router.push('/(app)/games')}
            />
          </View>
        </View>
      </View>

      </>}

      </HomePullReveal>

      <AppModal
        visible={tutorialWelcomeVisible}
        transparent
        animationType="fade"
        onRequestClose={() => void dismissTutorialWelcome()}
      >
        <View className="flex-1 bg-black/55 items-center justify-center px-5">
          <View className="w-full max-w-[420px] bg-paper border-2 border-olive rounded-2xl p-5 gap-4">
            <View className="items-center gap-2">
              <BrandMark kind="mark" height={64} variant={theme === 'dark' ? 'dark' : 'light'} />
              <Text className="text-blood font-stencil text-[11px] uppercase tracking-widest">
                Welcome aboard
              </Text>
              <Text className="text-ink font-stencil text-2xl uppercase text-center">
                Learn WarCarp in minutes
              </Text>
              <Text className="text-ink-soft font-typed text-sm text-center leading-5">
                Start with a short visual primer, then jump into a private Carpathia practice match whenever you are ready.
              </Text>
            </View>
            <View className="bg-canvas border border-olive rounded-xl p-3 gap-2">
              <Text className="text-ink font-stencil text-xs uppercase">The essentials</Text>
              <Text className="text-ink-soft font-typed text-xs leading-5">
                See how to place troops, attack across a border, move armies, fortify, and end your turn. You can try the real game afterward.
              </Text>
            </View>
            <Button
              title="Learn to Play"
              onPress={() => void startTutorialFromWelcome()}
              accessibilityLabel="Learn to play WarCarp"
            />
            <Button
              title="Not now"
              variant="secondary"
              onPress={() => void dismissTutorialWelcome()}
              accessibilityLabel="Dismiss welcome tutorial"
            />
          </View>
        </View>
      </AppModal>

      </LogoEggProvider>
    </SafeAreaView>
  )
}
